import { useState, useRef } from 'react';
import api from 'services/api';

const TYPES = [
  { value: 'agents', label: 'Agents de securite', desc: 'Matricule, nom, poste, site, telephone...' },
  { value: 'clients', label: 'Clients', desc: 'Nom entreprise, contact, telephone, offre...' },
  { value: 'incidents', label: 'Incidents', desc: 'Titre, type, gravite, date, actions...' },
];

const COL_INFO = {
  agents: {
    columns: [
      // ⚠️ L'ECRAN NE DOIT PLUS ANNONCER UNE CONTRAINTE QUI N'EXISTE PLUS.
      //    Il exigeait « respecter ce prefixe » : c'est precisement la regle
      //    retiree le 01/09. Un matricule apporte par le client est SA donnee.
      { key: 'Matricule', required: false, hint: 'Laissez vide pour une generation automatique. Sinon, gardez VOS matricules tels quels — seule l\'unicite est verifiee.' },
      { key: 'Nom', required: true, hint: 'Nom de famille de l\'agent' },
      { key: 'Prenom', required: true, hint: 'Prenom de l\'agent' },
      { key: 'Poste', required: false, hint: 'agent_jour | agent_nuit | chef_poste | superviseur | rondier' },
      // ⚠️ `Site` RETIRE le 31/08 : annonce ici et dans le modele, mais JAMAIS
      //    enregistre (aucune colonne site sur agents, aucune table de liaison).
      //    TOUTE COLONNE ANNONCEE EST ENREGISTREE, OU ELLE DISPARAIT DU MODELE
      //    *ET* DE L'ECRAN — les deux, sinon l'utilisateur saisit pour rien.
      { key: 'Telephone', required: false, hint: 'Numero de telephone avec indicatif (+228 ...)' },
      { key: 'Date_embauche', required: false, hint: 'Format AAAA-MM-JJ (ex: 2026-06-01)' },
      { key: 'Statut', required: false, hint: 'actif | inactif | conge — Par defaut : actif' },
    ],
  },
  clients: {
    columns: [
      { key: 'Nom_entreprise', required: true, hint: 'Raison sociale du client' },
      { key: 'Contact', required: true, hint: 'Nom du contact principal' },
      { key: 'Telephone', required: true, hint: 'Numero de telephone du contact' },
      { key: 'Email', required: false, hint: 'Adresse email du contact' },
      { key: 'Adresse', required: false, hint: 'Adresse physique du siege' },
      { key: 'Ville', required: false, hint: 'Ville (ex: Lome, Kara, Sokode)' },
      // ⚠️ `Offre`, `Tarif_mensuel` et `Statut` RETIRES le 31/08 : annonces mais
      //    jamais enregistres. `Statut` etait le pire — le client saisissait
      //    « suspendu » et le systeme ecrivait `actif` par defaut, soit
      //    L'INVERSE de sa saisie, sans un mot.
    ],
  },
  incidents: {
    columns: [
      { key: 'Titre', required: true, hint: 'Intitule court de l\'incident' },
      { key: 'Description', required: false, hint: 'Texte libre decrivant l\'incident en detail' },
      { key: 'Type', required: true, hint: 'intrusion | vol | agression | incendie | accident | autre' },
      { key: 'Gravite', required: false, hint: 'FAIBLE | MOYEN | ELEVE | CRITIQUE — Par defaut : moyenne' },
      { key: 'Statut', required: false, hint: 'ouvert | en_cours | resolu | ferme — Par defaut : ouvert' },
      { key: 'Date', required: false, hint: 'Format JJ/MM/AAAA ou AAAA-MM-JJ' },
      { key: 'Actions_prises', required: false, hint: 'Mesures immediates appliquees sur le terrain' },
      { key: 'Mesures_prises', required: false, hint: 'Actions preventives pour eviter la recurrence' },
    ],
  },
};

function ColumnBadge({ col }) {
  const [showHint, setShowHint] = useState(false);
  const hasHint = !!col.hint;

  return (
    <span
      style={{
        padding: '3px 10px', borderRadius: 4, fontSize: 12, position: 'relative',
        background: col.required ? '#FECACA' : '#E5E7EB',
        color: col.required ? '#991B1B' : '#555',
        fontWeight: col.required ? 700 : 400,
        cursor: hasHint ? 'pointer' : 'default',
        borderBottom: hasHint && !col.required ? '1px dashed #999' : 'none',
      }}
      onClick={() => hasHint && setShowHint(!showHint)}
      onMouseEnter={() => hasHint && setShowHint(true)}
      onMouseLeave={() => setShowHint(false)}
    >
      {col.key}{col.required ? ' *' : ''}
      {hasHint && showHint && (
        <span style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 6, padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 400,
          background: '#1A1A2E', color: '#FFF', whiteSpace: 'nowrap', zIndex: 100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', lineHeight: 1.4,
          pointerEvents: 'none',
        }}>
          {col.hint}
          <span style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: '5px solid #1A1A2E',
          }} />
        </span>
      )}
    </span>
  );
}

export default function ImportPage() {
  const [type, setType] = useState('agents');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  // Feuille CHOISIE par l'utilisateur. Envoyee a l'apercu ET a l'import : les
  // deux doivent lire la MEME feuille, sinon on previsualise l'une et on
  // importe l'autre.
  const [feuille, setFeuille] = useState('');
  // ⚠️ CE QU'ON FAIT DES LIGNES DEJA PRESENTES — CHOISI, JAMAIS DEVINE.
  //    « ignorer » preserve ce qui est en base, « mettre a jour » l'ecrase :
  //    les deux sont legitimes, aucun n'est un defaut raisonnable. Tant que
  //    l'utilisateur n'a pas tranche, l'import ne part pas.
  const [existants, setExistants] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('select');
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setPreview(null); setResult(null); setStep('select'); }
  };

  const handlePreview = async (nomFeuille) => {
    if (!file) return;
    // ⚠️ GARDE — `onClick={handlePreview}` passe l'EVENEMENT React en premier
    //    argument. Le 01/09, ce parametre est parti tel quel dans le FormData :
    //    le serveur recevait « [object Object] » et repondait « feuille
    //    introuvable ». La signature avait change sans que l'appelant
    //    `onClick` soit revu. On n'accepte donc QU'UNE CHAINE.
    const feuilleDemandee = typeof nomFeuille === 'string' ? nomFeuille : '';
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (feuilleDemandee) fd.append('feuille', feuilleDemandee);
      const res = await api.post(`/import/${type}/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'analyse du fichier');
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // MEME feuille qu'a l'apercu — sinon confiance fausse.
      if (feuille) fd.append('feuille', feuille);
      // Le choix sur l'existant part avec l'import. Le serveur REFUSE s'il
      // manque alors que le fichier contient des lignes deja presentes.
      if (existants) fd.append('existants', existants);
      const res = await api.post(`/import/${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setStep('done');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally { setLoading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/import/modele/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `modele-import-${type}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erreur lors du telechargement du modele');
    }
  };

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setStep('select'); setExistants('');
    if (fileRef.current) fileRef.current.value = '';
  };

  // L'import est bloque tant qu'une decision attendue n'est pas prise.
  const bilan = preview?.bilan;
  const choixRequis = !!bilan && bilan.dejaPresents > 0 && !existants;
  const aEcrire = bilan
    ? (existants === 'ignorer' ? bilan.nouveaux : bilan.nouveaux + bilan.dejaPresents)
    : (preview?.totalAImporter ?? preview?.totalRows ?? 0);

  const cols = COL_INFO[type].columns;

  return (
    <div>
      {/* ═══ SELECTION TYPE + UPLOAD ═══ */}
      <div className="content-card" style={{ padding: 24, marginBottom: 20 }}>
        <div className="content-card-title" style={{ marginBottom: 16 }}>Import de donnees en masse</div>

        {/* Type cards */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {TYPES.map(t => (
            <button key={t.value} onClick={() => { setType(t.value); reset(); }}
              style={{
                flex: 1, padding: '16px 12px', borderRadius: 12, border: `2px solid ${type === t.value ? '#8B1A1A' : '#E0E0E0'}`,
                background: type === t.value ? '#FEF2F2' : '#FFF', cursor: 'pointer', textAlign: 'center',
                transition: 'all 200ms',
              }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: type === t.value ? '#8B1A1A' : '#333', marginBottom: 4 }}>
                {t.label}
              </div>
              <div style={{ fontSize: 11, color: '#999' }}>{t.desc}</div>
            </button>
          ))}
        </div>

        {/* Upload + Download */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile}
            style={{ flex: 1, padding: 8, border: '1px solid #DDD', borderRadius: 8, fontSize: 13 }} />
          <button onClick={downloadTemplate}
            style={{ height: 36, padding: '0 16px', borderRadius: 8, border: '1px solid #8B1A1A', background: '#FFF', color: '#8B1A1A', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Telecharger modele Excel
          </button>
        </div>

        {/* Colonnes attendues avec infobulles */}
        <div style={{ padding: 12, background: '#F8F9FA', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', marginBottom: 8 }}>
            Colonnes attendues pour « {TYPES.find(t => t.value === type)?.label} »
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {cols.map(c => <ColumnBadge key={c.key} col={c} />)}
          </div>
          <div style={{ fontSize: 10, color: '#999', marginTop: 6 }}>* Colonnes obligatoires — Survolez les badges pour voir les valeurs acceptees</div>
        </div>

        {file && step === 'select' && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 13, color: '#555' }}>
              Fichier : <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} Ko)
            </div>
            {/* ⚠️ `onClick={handlePreview}` passait l'EVENEMENT React comme nom
                de feuille. Toujours envelopper quand la fonction prend un
                argument metier. */}
            <button onClick={() => handlePreview()} disabled={loading}
              style={{ height: 36, padding: '0 24px', borderRadius: 8, border: 'none', background: '#1B3A5C', color: '#FFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {loading ? 'Analyse en cours...' : 'Apercu avant import'}
            </button>
          </div>
        )}
      </div>

      {/* ═══ PREVIEW ═══ */}
      {step === 'preview' && preview && (
        <div className="content-card" style={{ padding: 24, marginBottom: 20 }}>
          <div className="content-card-title" style={{ marginBottom: 12 }}>
            Apercu — {preview.totalRows} lignes detectees
          </div>

          {/* ⚠️ QUELLE FEUILLE A ETE LUE — le lecteur ne prend QUE la premiere,
              et il le faisait EN SILENCE. Mesure du 31/08 : un classeur de 3
              feuilles (5, 12, 13 lignes) affichait « 5 lignes detectees », sans
              aucun moyen pour l'utilisateur de comprendre pourquoi. */}
          {preview.classeur && preview.classeur.feuilleLue && (
            <div style={{
              border: preview.classeur.avertissement ? '1px solid #F59E0B' : '1px solid var(--gss-border)',
              background: preview.classeur.avertissement ? '#FFFBEB' : 'var(--gss-bg-soft, #FAFAFA)',
              borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12.5, lineHeight: 1.5,
            }}>
              {/* ⚠️ UN AVERTISSEMENT QUI N'OUVRE AUCUNE ACTION LAISSE LE TRAVAIL
                  A L'UTILISATEUR. Dire « seule Janvier a ete lue » sans offrir
                  de choisir Mars oblige a retoucher le classeur pour un defaut
                  qui n'est pas le sien. Le selecteur relance l'apercu, et la
                  feuille choisie est celle qui sera IMPORTEE. */}
              {preview.classeur.feuilles && preview.classeur.feuilles.length > 1 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: '#555' }}>Feuille à importer :</span>
                  <select className="gss-input" style={{ maxWidth: 320, height: 32, fontSize: 12.5 }}
                    value={preview.classeur.feuilleLue}
                    disabled={loading}
                    onChange={(e) => { setFeuille(e.target.value); handlePreview(e.target.value); }}>
                    {preview.classeur.feuilles.map(f => (
                      <option key={f.nom} value={f.nom}>
                        {f.nom} — {f.lignes} ligne{f.lignes > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                  <span style={{ color: '#777', fontSize: 11.5 }}>
                    sur {preview.classeur.total} feuilles du classeur
                  </span>
                </div>
              ) : (
                <span style={{ color: '#555' }}>
                  Feuille lue : <strong>« {preview.classeur.feuilleLue} »</strong>
                </span>
              )}
              {preview.classeur.avertissement && (
                <div style={{ marginTop: 6, color: '#92400E', fontWeight: 600 }}>
                  ⚠ {preview.classeur.avertissement}
                </div>
              )}
            </div>
          )}

          {/* ⚠️ LA LIGNE D'EXEMPLE DE NOTRE PROPRE MODELE — ANNONCEE AVANT
              L'ECRITURE. Le 01/09, elle est entree en base comme un vrai agent
              (`AGBEKO Kofi Mawuena`), puis a fait echouer la seconde tentative
              d'import par doublon de matricule. Le modele ne la place plus dans
              la feuille de donnees, mais TOUS LES MODELES DEJA TELECHARGES la
              portent encore : c'est ce message qui protege ceux-la.
              Ton neutre, PAS une erreur : le defaut etait le notre. */}
          {preview.lignesExemple > 0 && (
            <div style={{ border: '1px solid #93C5FD', background: '#EFF6FF', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12.5, lineHeight: 1.5, color: '#1E3A8A' }}>
              <strong>
                {preview.lignesExemple === 1
                  ? 'La ligne d\'exemple du modele a ete reconnue : elle ne sera pas importee.'
                  : `${preview.lignesExemple} lignes d'exemple du modele ont ete reconnues : elles ne seront pas importees.`}
              </strong>
              <div style={{ marginTop: 4 }}>
                Elle est marquee <em>exemple</em> dans le tableau ci-dessous. Les anciens modeles la
                placaient dans la feuille de donnees ; si vous avez saisi vos propres donnees
                <strong> par-dessus</strong> cette ligne, elle sera importee normalement.
              </div>
            </div>
          )}

          {/* ⚠️ CE QUE L'IMPORT NE CHARGE PAS — AVANT L'ECRITURE, PAS APRES.
              Mesure du 31/08/2026 : sur un fichier de 18 colonnes, l'import
              « agents » en ecrit 8 et en ignore 11 EN SILENCE. Sans salaire de
              base, le moteur REFUSE l'agent (calculPur:648) : aucune paie,
              aucun contrat. Un utilisateur qui l'apprend apres avoir importe
              80 agents a peuple un annuaire en croyant peupler un employeur. */}
          {preview.nonCouvert && (
            <div style={{ border: '1px solid #FCA5A5', background: '#FEF2F2', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 800, color: '#B91C1C', marginBottom: 6 }}>
                ⚠ {preview.nonCouvert.titre}
              </div>
              <div style={{ fontSize: 12.5, color: '#7F1D1D', marginBottom: 12, lineHeight: 1.5 }}>
                {preview.nonCouvert.consequence}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {preview.nonCouvert.blocs.map(b => (
                  <div key={b.bloc} style={{ flex: '1 1 220px', minWidth: 200 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#B91C1C', marginBottom: 4 }}>{b.bloc}</div>
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#7F1D1D', lineHeight: 1.6 }}>
                      {b.champs.map(c => <li key={c}>{c}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              {preview.nonCouvert.trompeuses && preview.nonCouvert.trompeuses.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #FCA5A5', fontSize: 12, color: '#7F1D1D' }}>
                  <strong>Attention :</strong> {preview.nonCouvert.trompeuses.join(' · ')}
                </div>
              )}
              {preview.nonCouvert.aVenir && preview.nonCouvert.aVenir.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #FCA5A5', fontSize: 12, color: '#7F1D1D' }}>
                  <strong>Non couvert aujourd'hui :</strong> {preview.nonCouvert.aVenir.join(' · ')}
                </div>
              )}
            </div>
          )}

          {/* ═══ X NOUVEAUX · Y DEJA PRESENTS · Z REJETES — ANNONCE D'AVANCE ═══
              ⚠️ « Une operation connait l'etat existant avant d'agir. » L'import
                 ecrivait sans regarder : un fichier corrige et reimporte entrait
                 en collision avec ce qui etait deja passe. */}
          {bilan && (
            <div style={{ border: '1px solid #C7D2FE', background: '#EEF2FF', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3730A3', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                Ce que contient ce fichier, comparé à vos données
                {preview.cleReconnaissance && (
                  <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#6366F1' }}>
                    {' '}— reconnaissance sur « {preview.cleReconnaissance} »
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 140px', padding: 12, background: '#FFF', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color: '#16A34A' }}>{bilan.nouveaux}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>nouveaux</div>
                </div>
                <div style={{ flex: '1 1 140px', padding: 12, background: '#FFF', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color: '#CA8A04' }}>{bilan.dejaPresents}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>déjà présents</div>
                </div>
                <div style={{ flex: '1 1 140px', padding: 12, background: '#FFF', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color: bilan.rejetes > 0 ? '#DC2626' : '#999' }}>{bilan.rejetes}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>rejetés</div>
                </div>
              </div>

              {/* ⚠️ UN COMPTE SANS SON CONTENU N'INFORME DE RIEN. */}
              {bilan.motifsRejet?.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #C7D2FE' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#B91C1C', marginBottom: 4 }}>
                    Pourquoi ces lignes sont rejetées
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#7F1D1D', lineHeight: 1.6 }}>
                    {bilan.motifsRejet.map((m, i) => (
                      <li key={i}>Ligne {m.ligne} — {m.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ marginTop: 10, fontSize: 11, color: '#4338CA' }}>
                Ces trois nombres sont établis <strong>avant</strong> toute écriture. D'autres refus peuvent
                encore apparaître à l'enregistrement (formats, règles métier) : ils seront nommés dans le
                résultat.
                {!preview.cleReconnaissance && ' Ce type de données n\'a pas de clé de reconnaissance : toutes les lignes seront ajoutées.'}
              </div>
            </div>
          )}

          {/* ═══ LE CHOIX SUR L'EXISTANT — EXIGÉ, JAMAIS DEVINÉ ═══ */}
          {bilan && bilan.dejaPresents > 0 && (
            <div style={{
              border: `2px solid ${choixRequis ? '#F59E0B' : '#C7D2FE'}`,
              background: choixRequis ? '#FFFBEB' : '#FFF', borderRadius: 8, padding: 16, marginBottom: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: choixRequis ? '#92400E' : '#3730A3', marginBottom: 4 }}>
                {bilan.dejaPresents} ligne{bilan.dejaPresents > 1 ? 's' : ''} de ce fichier {bilan.dejaPresents > 1 ? 'existent' : 'existe'} déjà dans vos données.
                {' '}Que faut-il en faire ?
              </div>
              <div style={{ fontSize: 12, color: '#78350F', marginBottom: 12 }}>
                Ce choix vous appartient — le système ne le devine pas. Les lignes nouvelles seront ajoutées
                dans les deux cas.
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { v: 'ignorer', t: 'Ignorer les lignes déjà présentes', d: 'Vos données actuelles ne changent pas. Seules les nouvelles sont ajoutées.' },
                  { v: 'mettre_a_jour', t: 'Mettre à jour avec le fichier', d: 'Les colonnes présentes dans le fichier remplacent celles en base. Les autres ne bougent pas.' },
                ].map(o => (
                  <button key={o.v} onClick={() => setExistants(o.v)}
                    style={{
                      flex: '1 1 260px', textAlign: 'left', padding: 12, borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${existants === o.v ? '#8B1A1A' : '#E0E0E0'}`,
                      background: existants === o.v ? '#FEF2F2' : '#FFF',
                    }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: existants === o.v ? '#8B1A1A' : '#333' }}>{o.t}</div>
                    <div style={{ fontSize: 11.5, color: '#666', marginTop: 3, lineHeight: 1.4 }}>{o.d}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ padding: 12, background: '#F0FDF4', borderRadius: 8, flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: '#22C55E' }}>{preview.matchedColumns.length}</div>
              <div style={{ fontSize: 11, color: '#666' }}>Colonnes reconnues</div>
            </div>
            <div style={{ padding: 12, background: '#FEF9C3', borderRadius: 8, flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: '#CA8A04' }}>{preview.unmatchedColumns.length}</div>
              <div style={{ fontSize: 11, color: '#666' }}>Colonnes ignorees</div>
            </div>
            <div style={{ padding: 12, background: preview.missingRequired.length > 0 ? '#FEF2F2' : '#F0FDF4', borderRadius: 8, flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: preview.missingRequired.length > 0 ? '#DC2626' : '#22C55E' }}>
                {preview.missingRequired.length > 0 ? preview.missingRequired.join(', ') : 'OK'}
              </div>
              <div style={{ fontSize: 11, color: '#666' }}>Champs obligatoires</div>
            </div>
            {/* ⚠️ « Lignes a importer » doit dire ce qui sera ECRIT, pas ce que
                le fichier contient. Avec la ligne d'exemple ecartee, les deux
                nombres different — annoncer celui du fichier serait une promesse
                que l'import ne tient pas. */}
            <div style={{ padding: 12, background: '#F8F8F8', borderRadius: 8, flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: '#333' }}>
                {preview.totalAImporter ?? preview.totalRows}
              </div>
              <div style={{ fontSize: 11, color: '#666' }}>
                Lignes a importer
                {preview.lignesExemple > 0 && (
                  <span style={{ color: '#999' }}> · {preview.totalRows} lues</span>
                )}
              </div>
            </div>
          </div>

          {/* Preview table — 10 first rows, original data */}
          {preview.preview.length > 0 && (
            <div className="gss-table-wrap" style={{ marginBottom: 16, border: '1px solid #E5E7EB', borderRadius: 8 }}>
              <table style={{ width: '100%', minWidth: 800, borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px 10px', background: '#F5F5F5', textAlign: 'center', fontWeight: 700, fontSize: 10, color: '#999', width: 40 }}>#</th>
                    {Object.keys(preview.preview[0]).map(col => (
                      <th key={col} style={{
                        padding: '8px 10px', background: '#F5F5F5', textAlign: 'left', fontWeight: 700, fontSize: 11,
                        color: preview.matchedColumns.includes(col.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')) ? '#1B3A5C' : '#999',
                        whiteSpace: 'nowrap',
                      }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row, i) => {
                    // ⚠️ MONTRER UNE LIGNE QU'ON NE VA PAS ECRIRE, SANS LE DIRE,
                    //    C'EST PROMETTRE A L'ECRAN CE QU'ON NE TIENT PAS EN BASE.
                    const exemple = (preview.rangsExemple || []).includes(i);
                    return (
                      <tr key={i} style={{
                        background: exemple ? '#F3F4F6' : (i % 2 === 0 ? '#FFF' : '#FAFAFA'),
                        color: exemple ? '#9CA3AF' : 'inherit',
                        fontStyle: exemple ? 'italic' : 'normal',
                      }}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #F0F0F0', textAlign: 'center', color: '#999', fontSize: 11, whiteSpace: 'nowrap' }}>
                          {exemple ? 'ex.' : i + 1}
                        </td>
                        {Object.values(row).map((v, j) => (
                          <td key={j} style={{ padding: '6px 10px', borderBottom: '1px solid #F0F0F0', whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {j === 0 && exemple && (
                              <span style={{ marginRight: 6, padding: '1px 6px', borderRadius: 4, background: '#E5E7EB', color: '#4B5563', fontSize: 10, fontStyle: 'normal', fontWeight: 700 }}>
                                exemple — non importee
                              </span>
                            )}
                            {String(v ?? '')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {preview.totalRows > 10 && (
                <div style={{ padding: 8, textAlign: 'center', fontSize: 11, color: '#999', background: '#FAFAFA' }}>
                  ... et {preview.totalRows - 10} autres lignes
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={reset}
              style={{ height: 36, padding: '0 20px', borderRadius: 8, border: '1px solid #DDD', background: '#FFF', color: '#555', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Annuler
            </button>
            {(() => {
              // Le bouton annonce ce qui sera ECRIT, et ne part pas tant qu'une
              // decision attendue n'est pas prise.
              const bloque = loading || preview.missingRequired.length > 0 || choixRequis || aEcrire === 0;
              return (
                <button onClick={handleImport} disabled={bloque}
                  style={{
                    height: 36, padding: '0 24px', borderRadius: 8, border: 'none',
                    background: bloque && !loading ? '#CCC' : '#8B1A1A',
                    color: '#FFF', fontWeight: 700, fontSize: 13,
                    cursor: bloque ? 'default' : 'pointer',
                  }}>
                  {loading ? 'Import en cours...'
                    : choixRequis ? 'Choisissez d\'abord ce qu\'il faut faire des lignes déjà présentes'
                      : aEcrire === 0 ? 'Aucune ligne à écrire'
                        : existants === 'mettre_a_jour'
                          ? `Confirmer (${bilan.nouveaux} ajoutées, ${bilan.dejaPresents} mises à jour)`
                          : `Confirmer l'import (${aEcrire} ligne${aEcrire > 1 ? 's' : ''})`}
                </button>
              );
            })()}
          </div>
        </div>
      )}

      {/* ═══ RESULTAT ═══ */}
      {step === 'done' && result && (
        <div className="content-card" style={{ padding: 24 }}>
          <div className="content-card-title" style={{ marginBottom: 12 }}>Resultat de l'import</div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ padding: 16, background: '#F0FDF4', borderRadius: 12, flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 900, color: '#22C55E' }}>{result.imported}</div>
              <div style={{ fontSize: 13, color: '#666' }}>ligne{result.imported > 1 ? 's' : ''} ajoutee{result.imported > 1 ? 's' : ''}</div>
            </div>
            {/* ⚠️ CREE, MIS A JOUR ET IGNORE NE SE CONFONDENT PAS. Un total unique
                cacherait ce que l'import a fait AUX DONNEES DEJA EN BASE. */}
            {result.misAJour > 0 && (
              <div style={{ padding: 16, background: '#EEF2FF', borderRadius: 12, flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: '#4F46E5' }}>{result.misAJour}</div>
                <div style={{ fontSize: 13, color: '#666' }}>mise{result.misAJour > 1 ? 's' : ''} a jour</div>
              </div>
            )}
            {result.ignores > 0 && (
              <div style={{ padding: 16, background: '#FEF9C3', borderRadius: 12, flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: '#CA8A04' }}>{result.ignores}</div>
                <div style={{ fontSize: 13, color: '#666' }}>deja presente{result.ignores > 1 ? 's' : ''}, laissee{result.ignores > 1 ? 's' : ''} intacte{result.ignores > 1 ? 's' : ''}</div>
              </div>
            )}
            <div style={{ padding: 16, background: result.skipped > 0 ? '#FEF2F2' : '#F8F8F8', borderRadius: 12, flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 900, color: result.skipped > 0 ? '#DC2626' : '#999' }}>{result.skipped}</div>
              <div style={{ fontSize: 13, color: '#666' }}>{result.skipped > 0 ? 'erreurs' : 'aucune erreur'}</div>
            </div>
            {/* ⚠️ COMPTEUR SEPARE, PAS UNE ERREUR. La ligne d'exemple ecartee
                n'est pas une faute de l'utilisateur : le defaut etait le notre.
                La compter avec les erreurs lui reprocherait notre modele. */}
            {result.exemplesIgnores > 0 && (
              <div style={{ padding: 16, background: '#EFF6FF', borderRadius: 12, flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: '#2563EB' }}>{result.exemplesIgnores}</div>
                <div style={{ fontSize: 13, color: '#666' }}>
                  ligne{result.exemplesIgnores > 1 ? 's' : ''} d'exemple du modele, ecartee{result.exemplesIgnores > 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>

          {/* Erreurs detaillees */}
          {result.errors && result.errors.length > 0 && (
            <div style={{ marginBottom: 16, border: '1px solid #FECACA', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: '#FEF2F2', fontSize: 12, fontWeight: 700, color: '#991B1B' }}>
                Detail des erreurs ({result.errors.length})
              </div>
              <div className="gss-table-wrap" style={{ maxHeight: 200 }}>
                <table style={{ width: '100%', minWidth: 500, borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '6px 10px', background: '#FFF5F5', textAlign: 'left', fontWeight: 700, color: '#991B1B', fontSize: 11 }}>Ligne</th>
                      <th style={{ padding: '6px 10px', background: '#FFF5F5', textAlign: 'left', fontWeight: 700, color: '#991B1B', fontSize: 11 }}>Colonne</th>
                      <th style={{ padding: '6px 10px', background: '#FFF5F5', textAlign: 'left', fontWeight: 700, color: '#991B1B', fontSize: 11 }}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i}>
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid #FEE2E2', fontWeight: 700 }}>{e.ligne}</td>
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid #FEE2E2', color: '#8B1A1A' }}>{e.colonne}</td>
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid #FEE2E2', color: '#555' }}>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button onClick={reset}
            style={{ height: 36, padding: '0 24px', borderRadius: 8, border: 'none', background: '#8B1A1A', color: '#FFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Nouvel import
          </button>
        </div>
      )}
    </div>
  );
}
